/**
 * The words for invite-only access (GH-871a, DEC-052), in one place for donor-web and iOS to use
 * verbatim when the client slice builds them. Kept apart from `MEMBERSHIP_COPY` on purpose: these
 * appear on event pages and the sign-in sheet, which are not membership surfaces, and the
 * scarcity exception DEC-051 allows on membership surfaces does not extend to them.
 *
 * Rules `access-copy.test.ts` enforces:
 *   - every on-screen label (`ACCESS_LABELS`) is five words or fewer; longer lines are body text
 *     or sit behind an ⓘ (DESIGN.md "Five words on screen");
 *   - no faith's vocabulary, no money, no urgency, no scarcity ("left", "only", "hurry", "spots",
 *     "waiting") — the event-page lines stand beside a Give button, and DEC-051 keeps scarcity off
 *     every give surface;
 *   - giving is never described as needing an invite or an account.
 *
 * The client slice adds the Swift twin and a parity test, as `MEMBERSHIP_COPY` has.
 */
export const ACCESS_COPY = {
    /* ------------------------------------------- an event, signed out, no QR or location */
    eventOpensAtEvent: "Opens at the event",
    eventOpensAtEventBody: "Scan the code on the screen there, or share your location when you arrive. You can still give any time.",
    useMyLocation: "Use my location",
    noOpenEventHere: "No open event here",
    noOpenEventHereBody: "We couldn’t find an event happening where you are. Scan the code on the screen instead.",
    /* GH-871b: the browser refused the position, or could not get one. */
    locationOff: "Location is turned off",
    locationOffBody: "Allow location for this site in your browser’s settings, or scan the code on the screen.",
    locationUnavailable: "Couldn’t find your location",
    locationUnavailableBody: "Try again in a moment, or scan the code on the screen.",
    /* Listings (directory rows, an organization's events) while they are hidden. */
    eventsForMembers: "Events are for members",
    eventsForMembersBody: "Members see what’s coming up. Anyone at an event can open it there, and everyone can give.",
    /* -------------------------------------------------- signing up without an invite */
    inviteRequired: "Sign-up is by invite",
    inviteRequiredBody: "Enter an invite code from a member, or join the waitlist and we’ll let you know when it’s your turn.",
    /* ------------------------------------------------- the waitlist, without an account */
    waitlistContact: "Your phone or email",
    sendCode: "Send code",
    enterCodeWeSent: "Enter the code we sent",
    waitlistNoAccount: "No account is created until you’re admitted.",
    waitlistSmsNotice: "We’ll text you once, when it’s your turn. Message and data rates may apply.",
    waitlistEmailNotice: "We’ll email you once, when it’s your turn.",
    waitlistAdmitted: "Your invite is coming",
    waitlistCanSignIn: "You already have an account",
    /* ------------------------------------- the signed-out home, while membership is on (GH-871b) */
    /*
     * Owner, 2026-10-03: "In logged out mode, app home page needs to very prominently show invite
     * only and ask for access code - not wait till logged in or signed up." The same strings on the
     * web Home and the iOS Home; the give directory stays below the card, never behind the code.
     */
    home: {
        title: "Invite only",
        body: "GiveHere is opening by invitation. Enter your code to join.",
        codeLabel: "Access code",
        continue: "Continue",
        waitlist: "No code? Join the waitlist",
        signIn: "Have an account? Sign in",
        giveNote: "You can still give without an account.",
        /*
         * GH-879 (owner, 2026-10-04): the invite-only welcome — "Logo, app name and tag line" above
         * the code. The owner typed "Easies"; "Easiest" is what was meant.
         */
        tagline: "Easiest way to donate HERE!",
        /* Under "<Inviter> invited you" on the sign-up sheet a good code opens. */
        signUpLede: "Your invite is used once you’re signed in.",
    },
    /* ------------------------------------- the big screen, opened without the console's link */
    /*
     * GH-871e (owner, 2026-10-03: "yes" to a hint on an unpaired big screen). A muted line in
     * the wall's top margin, in small type, while membership is on and the API refuses the screen as not paired — read by
     * staff setting up, but seen by the hall, so it names no event and no person and alarms nobody.
     * "Big screen" and "Reset screen links" are the console's own labels (`big-screen-link.tsx`).
     * Web only: the wall is a browser surface; the app has no big screen.
     */
    screenNotPaired: "Screen not paired",
    screenNotPairedBody: "Open this screen from the console’s “Big screen” link. “Reset screen links” retires earlier ones.",
    /* ------------------------------------------------- the admission message (server-side) */
    admissionSubject: "It’s your turn on GiveHere",
    /** GH-871d: the code is usable for 30 days (owner, 2026-10-03); `useBy` is a date a person reads. */
    admissionMessage: (code, link, useBy) => `It’s your turn on GiveHere. Your invite code is ${code}. Use it by ${useBy}. Sign up here: ${link}`,
};
/** What sits on screen as a label or button — five words or fewer each. */
export const ACCESS_LABELS = [
    ACCESS_COPY.eventOpensAtEvent,
    ACCESS_COPY.useMyLocation,
    ACCESS_COPY.noOpenEventHere,
    ACCESS_COPY.locationOff,
    ACCESS_COPY.locationUnavailable,
    ACCESS_COPY.eventsForMembers,
    ACCESS_COPY.inviteRequired,
    ACCESS_COPY.waitlistContact,
    ACCESS_COPY.sendCode,
    ACCESS_COPY.enterCodeWeSent,
    ACCESS_COPY.waitlistAdmitted,
    ACCESS_COPY.waitlistCanSignIn,
    ACCESS_COPY.home.title,
    ACCESS_COPY.home.codeLabel,
    ACCESS_COPY.home.continue,
    ACCESS_COPY.home.waitlist,
    ACCESS_COPY.home.signIn,
    ACCESS_COPY.home.tagline,
    ACCESS_COPY.screenNotPaired,
];
/**
 * The words a person saw beside the waitlist's text-message notice, recorded with the SMS consent
 * for the `waitlist_admission` purpose. Bump it whenever `waitlistSmsNotice` changes.
 */
export const WAITLIST_SMS_DISCLOSURE_VERSION = "2026-10-03.1";
//# sourceMappingURL=access-copy.js.map